import type { ProjectListItem } from '../../types';
import { showRenameProjectModal } from '../modals/renameProject';
import { createProjectMenu } from './ProjectMenu';

export interface ProjectListViewCallbacks {
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, newName: string) => void;
}

export class ProjectListView {
  private readonly container: HTMLElement;
  private readonly callbacks: ProjectListViewCallbacks;
  private projects: ProjectListItem[] = [];
  private currentProjectId: string | null = null;
  private openMenuId: string | null = null;

  constructor(container: HTMLElement, callbacks: ProjectListViewCallbacks) {
    this.container = container;
    this.callbacks = callbacks;

    document.addEventListener('click', this.handleDocumentClick);
    this.render();
  }

  setProjects(projects: ProjectListItem[]): void {
    this.projects = projects;
    this.render();
  }

  setCurrentProject(id: string | null): void {
    this.currentProjectId = id;
    this.render();
  }

  getCurrentProjectId(): string | null {
    return this.currentProjectId;
  }

  private handleDocumentClick = (e: MouseEvent): void => {
    if (this.openMenuId && !(e.target as HTMLElement).closest('.project-menu')) {
      this.closeOpenMenu();
    }
  };

  private render(): void {
    this.container.innerHTML = '';

    if (this.projects.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'project-empty';
      empty.textContent = 'No saved sessions';
      this.container.appendChild(empty);
      return;
    }

    for (const project of this.projects) {
      const item = this.createProjectItem(project);
      this.container.appendChild(item);
    }
  }

  private createProjectItem(project: ProjectListItem): HTMLDivElement {
    const item = document.createElement('div');
    item.className = 'project-item';
    if (project.id === this.currentProjectId) {
      item.classList.add('active');
    }

    const thumbnail = document.createElement('img');
    thumbnail.className = 'project-thumbnail';
    thumbnail.src = project.thumbnail;
    thumbnail.alt = project.name;

    const info = document.createElement('div');
    info.className = 'project-info';

    const name = document.createElement('span');
    name.className = 'project-name';
    name.textContent = project.name;

    const date = document.createElement('span');
    date.className = 'project-date';
    date.textContent = this.formatDate(project.updatedAt);

    info.appendChild(name);
    info.appendChild(date);

    const handleMenuToggle = (projectId: string, container: HTMLElement): void =>
      this.toggleMenu(projectId, container);
    const handleRename = (): void => {
      this.closeOpenMenu();
      showRenameProjectModal(project.name, (newName) => {
        if (newName.trim() !== project.name) {
          this.callbacks.onRename(project.id, newName.trim());
        }
      });
    };
    const handleDelete = (): void => {
      this.closeOpenMenu();
      this.callbacks.onDelete(project.id);
    };

    const menuContainer = createProjectMenu(project.id, {
      onToggle: handleMenuToggle,
      onRename: handleRename,
      onDelete: handleDelete,
    });

    item.appendChild(thumbnail);
    item.appendChild(info);
    item.appendChild(menuContainer);

    item.addEventListener('click', () => {
      this.callbacks.onLoad(project.id);
    });

    return item;
  }

  private toggleMenu(projectId: string, container: HTMLElement): void {
    if (this.openMenuId === projectId) {
      this.closeOpenMenu();
    } else {
      this.closeOpenMenu();
      this.openMenuId = projectId;
      container.classList.add('open');
      container.closest('.project-item')?.classList.add('menu-open');
    }
  }

  private closeOpenMenu(): void {
    if (this.openMenuId) {
      const openContainer = document.querySelector('.project-menu.open');
      openContainer?.classList.remove('open');
      openContainer?.closest('.project-item')?.classList.remove('menu-open');
      this.openMenuId = null;
    }
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
}
